import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, ShieldCheck, Truck, RefreshCcw, Quote, Smartphone, SlidersHorizontal } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { SEO } from "../components/SEO";
import { MagazineCard, Stars, ReadSampleLink } from "../components/site/MagazineCard";
import { useReveal } from "../lib/useReveal";
import { toast } from "sonner";
import { SafeImg } from "../components/site/SafeImg";
import { FieldError } from "../components/site/FieldError";
import { validators } from "../lib/validation";

const HERO_BGS = [
  "https://images.unsplash.com/photo-1704391904759-11e28009335d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1713788833251-3a5165de100b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

const TRUST = [
  { icon: Smartphone, t: "Digital + Print", d: "Every plan includes both editions." },
  { icon: SlidersHorizontal, t: "Flexible Plans", d: "6, 12 or 24 months - your call." },
  { icon: ShieldCheck, t: "Secure Payments", d: "UPI, cards & netbanking via Razorpay." },
  { icon: RefreshCcw, t: "Easy Cancellation", d: "Cancel anytime, no questions asked." },
  { icon: Truck, t: "Pan-India Delivery", d: "Doorstep delivery, every PIN code." },
];

const TESTIMONIALS = [
  { name: "Meera Krishnan", city: "Chennai", text: "My mother waits for Divya Bhakti every month. BookMala renewed our subscription in two minutes and it has never missed an issue." },
  { name: "Arjun Malhotra", city: "Gurugram", text: "Business Mantra at my doorstep every Monday and the digital edition on my phone. The flip reader feels like holding the real magazine." },
  { name: "Sunita Deshmukh", city: "Pune", text: "Subscribed to Champak Junior for my daughter. She reads cover to cover. Easy payment via UPI, zero hassle." },
];

const Hero = ({ featured }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (featured.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % featured.length), 6500);
    return () => clearInterval(t);
  }, [featured.length]);
  if (!featured.length) return null;
  const m = featured[idx];
  return (
    <section className="relative overflow-hidden bg-bm-ink" data-testid="hero-carousel">
      {HERO_BGS.map((bg, i) => (
        <img key={bg} src={bg} alt="" aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === idx % HERO_BGS.length ? "opacity-20" : "opacity-0"}`} />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-bm-ink via-bm-ink/80 to-bm-ink/30" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-28 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <span className="inline-block eyebrow !text-bm-sand border border-bm-sand/40 rounded-full px-4 py-1.5 mb-8" data-testid="hero-badge">
            {m.category.replace(/-/g, " ")} · Featured Issue
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] tracking-tight" data-testid="hero-title">
            {m.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#D1D1D1] max-w-xl leading-relaxed">{m.short_description}</p>
          <div className="flex items-center gap-3 mt-5">
            <Stars value={m.rating} />
            <span className="text-sm text-[#A8A8A8]">{(m.rating_count || 0).toLocaleString("en-IN")} readers</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-10">
            <Link to={`/magazines/${m.slug}`} data-testid="hero-subscribe-btn" className="btn-primary">
              {m.price_from != null ? `Subscribe from ₹${m.price_from.toLocaleString("en-IN")}` : "Subscribe Now"}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <ReadSampleLink slug={m.slug} />
          </div>
          {featured.length > 1 && (
            <div className="flex items-center gap-3 mt-12">
              <button onClick={() => setIdx((idx - 1 + featured.length) % featured.length)} data-testid="hero-prev-btn" aria-label="Previous"
                className="w-10 h-10 rounded-full border border-[#3A3A3A] text-[#B0B0B0] flex items-center justify-center hover:border-bm-sand hover:text-bm-sand transition-colors duration-200">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => setIdx((idx + 1) % featured.length)} data-testid="hero-next-btn" aria-label="Next"
                className="w-10 h-10 rounded-full border border-[#3A3A3A] text-[#B0B0B0] flex items-center justify-center hover:border-bm-sand hover:text-bm-sand transition-colors duration-200">
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="flex gap-2 ml-2">
                {featured.map((f, i) => (
                  <button key={f.id} onClick={() => setIdx(i)} data-testid={`hero-dot-${i}`} aria-label={`Slide ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-bm-sand" : "w-3 bg-[#3A3A3A]"}`} />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center">
          <Link to={`/magazines/${m.slug}`} className="relative block" data-testid="hero-cover-link">
            <div className="absolute -inset-4 bg-bm-sand/15 rotate-3 rounded-xl" />
            <SafeImg src={imgUrl(m.cover_image)} alt={m.title}
              className="relative w-64 sm:w-80 aspect-[3/4] object-cover rounded-xl shadow-cover-hover -rotate-2 hover:rotate-0 transition-transform duration-500" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const TABS = [
  ["new", "New Releases"],
  ["popular", "Most Popular"],
  ["editors", "Editor's Picks"],
  ["coming", "Coming Soon"],
];

const NewsletterStrip = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const msg = validators.email(email);
    setError(msg);
    if (msg) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email: email.trim().toLowerCase() });
      toast.success("Subscribed! See you in your inbox.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    }
    setBusy(false);
  };
  return (
    <section className="bg-bm-sand" data-testid="newsletter-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div className="reveal">
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-bm-ink">Stay in the loop</h2>
          <p className="text-bm-body mt-3 max-w-md leading-relaxed">New issues, editor's picks and subscriber-only offers - one thoughtful letter a month. No noise.</p>
        </div>
        <form onSubmit={submit} className="reveal" data-testid="strip-newsletter-form" noValidate>
          <div className="flex flex-col sm:flex-row gap-3">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              onBlur={() => email && setError(validators.email(email))}
              placeholder="Your email address" data-testid="strip-newsletter-input" aria-invalid={!!error}
              className={`flex-1 bg-white border rounded-lg px-5 py-3.5 text-sm text-bm-ink focus:outline-none transition-colors duration-200 ${error ? "border-red-400" : "border-transparent focus:border-bm-terra"}`} />
            <button type="submit" disabled={busy} data-testid="strip-newsletter-btn" className="btn-primary shrink-0">
              {busy ? "Subscribing..." : "Subscribe"}
            </button>
          </div>
          <FieldError msg={error} id="strip-newsletter" />
        </form>
      </div>
    </section>
  );
};

export default function HomePage() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("new");
  useEffect(() => { api.get("/home").then(setData).catch(() => {}); }, []);
  useReveal([data, tab]);
  if (!data) return <div className="py-32 text-center text-bm-muted" data-testid="home-loading">Loading BookMala...</div>;
  const { featured, new_arrivals, popular, coming_soon = [], publishers, posts = [] } = data;
  const tabData = { new: new_arrivals, popular, editors: featured, coming: coming_soon };
  const pick = featured[0];
  return (
    <div data-testid="home-page">
      <SEO title="" path="/" />
      <Hero featured={featured} />

      <section className="bm-section" data-testid="discover-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="reveal max-w-2xl mb-10">
            <span className="eyebrow">The Catalog</span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold mt-3 text-bm-ink">Discover Your Next Issue</h2>
          </div>
          <div className="reveal flex flex-wrap gap-3 mb-12" data-testid="discover-tabs">
            {TABS.map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`discover-tab-${k}`}
                className={`chip cursor-pointer ${tab === k ? "chip-active" : "hover:bg-bm-sand-dark"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
            {(tabData[tab] || []).slice(0, 8).map((m) => <MagazineCard key={m.id} mag={m} />)}
          </div>
          {!(tabData[tab] || []).length && <p className="text-center text-bm-muted py-16" data-testid="discover-empty">Nothing here yet - check back soon.</p>}
        </div>
      </section>

      <section className="bg-white border-y border-bm-line" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
          {TRUST.map((b) => (
            <div key={b.t} className="reveal text-center" data-testid={`trust-${b.t.toLowerCase().replace(/[\s+]+/g, "-")}`}>
              <b.icon className="w-8 h-8 mx-auto text-bm-terra" strokeWidth={1.5} />
              <h3 className="font-display font-semibold text-base mt-3 text-bm-ink">{b.t}</h3>
              <p className="text-sm text-bm-muted mt-1">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bm-section" data-testid="publisher-spotlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="reveal flex items-end justify-between mb-12">
            <div>
              <span className="eyebrow">Our Publishing Partners</span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold mt-3 text-bm-ink">Houses behind the headlines</h2>
            </div>
            <Link to="/publishers" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-bm-terra hover:text-bm-terra-dark transition-colors duration-200" data-testid="view-all-publishers">
              All publishers <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {publishers.map((p) => (
              <Link key={p.id} to={`/publishers/${p.slug}`} data-testid={`publisher-spotlight-${p.slug}`}
                className="reveal bm-card p-8 text-center transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-card-hover">
                <SafeImg src={imgUrl(p.logo)} alt={p.name} className="w-20 h-20 mx-auto rounded-lg object-cover shadow-cover" loading="lazy" />
                <div className="font-display font-semibold text-lg mt-5 text-bm-ink">{p.name}</div>
                <div className="text-sm text-bm-muted mt-1">Est. {p.founded_year}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <NewsletterStrip />

      {pick && (
        <section className="bm-section" data-testid="editors-pick">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <div className="reveal bm-card overflow-hidden grid grid-cols-1 lg:grid-cols-12">
              <div className="lg:col-span-5 bg-bm-sand/50 flex items-center justify-center p-10 lg:p-16">
                <SafeImg src={imgUrl(pick.cover_image)} alt={pick.title} className="w-56 sm:w-72 aspect-[3/4] object-cover rounded-xl shadow-cover-hover rotate-2" loading="lazy" />
              </div>
              <div className="lg:col-span-7 p-10 lg:p-16 flex flex-col justify-center">
                <span className="eyebrow">Editor's Pick of the Month</span>
                <h2 className="font-display text-3xl md:text-4xl font-semibold mt-4 text-bm-ink">{pick.title}</h2>
                <p className="text-bm-body mt-5 leading-relaxed max-w-lg">{pick.short_description}</p>
                <div className="flex items-center gap-3 mt-5">
                  <Stars value={pick.rating} />
                  <span className="text-sm text-bm-muted">{(pick.rating_count || 0).toLocaleString("en-IN")} ratings</span>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-8">
                  {pick.price_from != null && (
                    <span className="font-display text-3xl font-bold text-bm-ink">₹{pick.price_from.toLocaleString("en-IN")}<span className="text-sm font-body font-normal text-bm-muted"> onwards</span></span>
                  )}
                  <Link to={`/magazines/${pick.slug}`} data-testid="editors-pick-cta" className="btn-primary">
                    Subscribe Now <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="pb-16 md:pb-20 lg:pb-24" data-testid="whats-new-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="reveal flex items-end justify-between mb-10">
            <div>
              <span className="eyebrow">Fresh Off The Press</span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold mt-3 text-bm-ink">What's New</h2>
            </div>
            <Link to="/magazines" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-bm-terra hover:text-bm-terra-dark transition-colors duration-200" data-testid="view-all-magazines">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-4">
            {new_arrivals.slice(0, 3).map((m) => (
              <Link key={m.id} to={`/magazines/${m.slug}`} data-testid={`whats-new-${m.slug}`}
                className="reveal bm-card flex items-center gap-5 p-4 transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-card-hover">
                <SafeImg src={imgUrl(m.cover_image)} alt={m.title} className="w-14 aspect-[3/4] object-cover rounded-md shadow-cover" loading="lazy" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-semibold text-lg text-bm-ink truncate">{m.title}</h3>
                  <p className="text-sm text-bm-muted truncate">{m.short_description}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="chip">{m.category.replace(/-/g, " ")}</span>
                  {m.price_from != null && <div className="text-sm font-semibold text-bm-ink mt-2">₹{m.price_from.toLocaleString("en-IN")} onwards</div>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {posts.length > 0 && (
        <section className="bg-bm-sand/40 bm-section" data-testid="blog-teasers">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <div className="reveal flex items-end justify-between mb-12">
              <div>
                <span className="eyebrow">From The Journal</span>
                <h2 className="font-display text-3xl md:text-4xl font-semibold mt-3 text-bm-ink">Reading guides & roundups</h2>
              </div>
              <Link to="/blog" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-bm-terra hover:text-bm-terra-dark transition-colors duration-200" data-testid="view-all-posts">
                All stories <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-teaser-${p.slug}`}
                  className="reveal bm-card p-8 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-card-hover">
                  <span className="text-xs text-bm-muted uppercase tracking-[0.1em] font-semibold">{(p.published_at || "").slice(0, 10)}</span>
                  <h3 className="font-display font-semibold text-xl mt-3 text-bm-ink leading-snug">{p.title}</h3>
                  <p className="text-sm text-bm-body mt-3 leading-relaxed flex-1">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-bm-terra mt-5">Read more <ArrowRight className="w-4 h-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bm-section" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="reveal text-center max-w-2xl mx-auto mb-14">
            <span className="eyebrow">Kind Words</span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold mt-3 text-bm-ink">Readers on BookMala</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <figure key={t.name} className="reveal bm-card p-8" data-testid={`testimonial-${t.name.toLowerCase().split(" ")[0]}`}>
                <Quote className="w-7 h-7 text-bm-terra mb-5" strokeWidth={1.5} />
                <blockquote className="font-display text-lg leading-relaxed text-bm-body italic">"{t.text}"</blockquote>
                <figcaption className="mt-5 text-sm font-semibold text-bm-ink">{t.name} <span className="font-normal text-bm-muted">· {t.city}</span></figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
